const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export async function streamChat({ message, sessionId, onToken, onReset, onDone, onError }) {
  try {
    const res = await fetch(`${API}/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message, session_id: sessionId }),
    });
    if (!res.ok || !res.body) {
      const data = await res.json().catch(() => ({}));
      onError(data.detail || "Revelle is unavailable right now. Please try again.");
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split("\n\n");
      buffer = events.pop();
      for (const evt of events) {
        const line = evt.trim();
        if (!line.startsWith("data:")) continue;
        let data;
        try { data = JSON.parse(line.slice(5)); } catch { continue; }
        if (data.token) onToken(data.token);
        else if (data.reset) onReset && onReset();
        else if (data.done) onDone(data);
        else if (data.error) onError(data.error);
      }
    }
  } catch (e) {
    onError("Connection issue - please try again.");
  }
}

export async function fetchTTSAudio(text) {
  const res = await fetch(`${API}/tts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error("tts_unavailable");
  const blob = await res.blob();
  if (blob.size < 200) throw new Error("tts_unavailable");
  return URL.createObjectURL(blob);
}

export function getAdminToken() {
  return localStorage.getItem("revel_admin_token") || "";
}

export async function adminFetch(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getAdminToken()}`,
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) {
    localStorage.removeItem("revel_admin_token");
    throw new Error("session_expired");
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(typeof data.detail === "string" ? data.detail : "Request failed");
  }
  return res;
}

export async function adminLogin(username, password) {
  const res = await fetch(`${API}/admin/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Login failed");
  localStorage.setItem("revel_admin_token", data.token);
  return data;
}

export async function submitLead(payload) {
  const res = await fetch(`${API}/leads`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Submission failed");
  return data;
}

export function loadRecaptcha(siteKey) {
  return new Promise((resolve) => {
    if (!siteKey) return resolve();
    if (window.grecaptcha) return resolve();
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => (window.grecaptcha ? window.grecaptcha.ready(resolve) : resolve());
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });
}

export async function getRecaptchaToken(action = "lead_submit") {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  if (!siteKey) return undefined;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      await loadRecaptcha(siteKey);
      if (!window.grecaptcha) return undefined;
      const token = await window.grecaptcha.execute(siteKey, { action });
      if (token) return token;
    } catch { /* retry once */ }
  }
  return undefined;
}
